import React, { useContext } from 'react';
import { useParams } from 'react-router-dom';
import LazyLoad from 'react-lazyload';
import Scroll from 'react-scroll';
import ConfigContext from '../../../contexts/ConfigContext';
import { useGetLocationByIdQuery } from '../../../redux/slices/locationSlice';
import useMapLink from '../../../hooks/useMapLink';

// Components
import PublicEvents from '../events/PublicEvents';
import Hero from '../content/Hero';
import Meta from '../../shared/Meta';
import Loader from '../../shared/Loader';
import ScrollNav from '../ScrollNav';
import QuoteCta from '../content/QuoteCta';
import LocationSidebar from './LocationSidebar';
import DisqusReviews from '../content/DisqusReviews';
import CateringFaq from '../faqs/CateringFaq';
import FeaturedPods from '../FeaturedPods';
import SimpleCta from '../content/SimpleCta';
import CateringCta from '../content/CateringCta';

// Misc
import { cityState } from '../../../modules/helpers';

// Styles
import sidebarStyles from '../../../../stylesheets/public/layouts/SidebarLayout.module.postcss';
import styles from './Location.module.postcss';

const OFFSET = -100;

function Location() {
  const { id } = useParams();
  const { imageBase, site_title, city_name, vehicle_type, support_email } = useContext(ConfigContext);

  const { data: location = {}, isLoading } = useGetLocationByIdQuery(id);
  const mapLink = useMapLink(location);

  let image = null;
  if (location && !isLoading && location.photo) {
    image = `${imageBase}/${location.photo}`;
  } else if (location && location.id) {
    image = 'https://static.seattlefoodtruck.com/photos/locationheader.png';
  }

  let name = '';
  if (location && !isLoading) {
    name = location.name ? location.name : location.filtered_address;
  }

  let title = site_title;
  let description = '';
  if (location) {
    const cityStateValue = cityState(location.address);
    const neighborhood = location.neighborhood ? location.neighborhood.name + ' -' : '';
    title = `${name} - Food ${vehicle_type} Schedule - ${neighborhood} ${cityStateValue} - Food ${vehicle_type} Reviews - Phone Number - Menu`;
    if (location.current_trucks) {
      description = `${location.filtered_address} - ${location.current_trucks}`;
    } else {
      description = `${location.filtered_address}`;
    }
  }

  const links = [
    { title: 'Schedule', to: 'schedule' },
    ...(city_name === 'Seattle' ? [{ title: 'About', to: 'about' }] : []),
    { title: 'Reviews', to: 'reviews' },
  ];

  return (
    <div>
      { location &&
        <Meta
          title={title}
          description={description}
          image={image}
          type='website'
          breadcrumbName={location.name}
        />
      }

      <Hero photo={image} />

      <ScrollNav links={links} offset={OFFSET} isLocation />

      <div className='Section Section--extraPadding'>
        <div className={sidebarStyles.SidebarLayout}>
          <div className={sidebarStyles.SidebarLayout_body}>
            <Loader isLoading={isLoading || !location} padded>

              <h1>{location.name}</h1>
              <a href={mapLink} className={styles.Location_Address}>
                <div>
                  <div><img src='/static/images/icons/pin.svg' alt='Address'/></div>
                  <div>{location.address}</div>
                </div>
              </a>

              <div className='InnerSection u-pt3 u-pb1'>
                <h2 className='u-mb1'>🚚 {city_name}&#39;s Best Food {vehicle_type}s — Find & Book Now!</h2>
                <p>Planning an event? Whether it&#39;s a wedding, corporate gathering, private party, or festival, we make booking fast, easy, and stress-free. {site_title} connects you with top-rated food {vehicle_type.toLowerCase()}s serving authentic, diverse, and delicious meals customized for your event.</p>
              </div>

              <div className='InnerSection'>
                <QuoteCta />
              </div>

              <Scroll.Element name='schedule' className='InnerSection'>
                <PublicEvents
                  locationId={location.id}
                  location={location}
                  image={image}
                  scrollTo='schedule'
                />
              </Scroll.Element>

              { city_name === 'Seattle' &&
                <Scroll.Element name='about' className='InnerSection'>
                  <h2>🚚 Seattle&#39;s Food Truck Scene: Where Flavor Meets Innovation</h2>
                  <p>Seattle isn&#39;t just about coffee and rain – it&#39;s a city that thrives on bold flavors, local ingredients, and creative street food. From the tech hubs of South Lake Union to the laid-back breweries in Ballard, food trucks have become a defining part of the city&#39;s culinary scene.</p>
                  <p>With a culture that values fresh, locally sourced food, Seattleites don&#39;t settle for just any meal. They want handcrafted burgers made from grass-fed beef, Thai curries bursting with spice, tacos that rival Mexico City, and wood-fired pizzas that transport you straight to Naples. Thankfully, Seattle&#39;s food trucks deliver exactly that.</p>
                  <p>Now that the food truck movement has taken over the city, there are almost too many options to choose from. From global fusion to classic American comfort food, each truck brings something unique to the table. But what unites them all is a commitment to artisanal flavors, high-quality ingredients, and forward-thinking culinary techniques.</p>
                </Scroll.Element>
              }

              <Scroll.Element name='reviews' className='InnerSection'>
                <h2>Customer Reviews</h2>
                <LazyLoad height={500} offset={100} once>
                  <DisqusReviews location={location} />
                </LazyLoad>
              </Scroll.Element>

              <div className='InnerSection'>
                <CateringFaq location={location} />
              </div>

            </Loader>

          </div>
          <div className={sidebarStyles.SidebarLayout_sidebar}>
            <LocationSidebar location={location} />
          </div>
        </div>
      </div>

      { city_name === 'Seattle' &&
        <section className='Section'>
          <FeaturedPods />
        </section>
      }

      <section className='Section'>
        <SimpleCta
          text='Tell us how we can improve our site'
          link={'mailto:' + support_email}
          linkTitle='Share Feedback'
          linkIsExternal
        />
      </section>

      <section className='Section Section--extraPadding u-pt2'>
        <CateringCta />
      </section>

    </div>
  );
}

export default Location;
